import { generateAndDownloadPaystub } from "./paystubGenerator";
import { isUserLoggedIn, userHeaders } from "./userAuth";
import { markNotificationReady, setNotificationDocId } from "./appNotifications";
import { emitAppLive } from "./appLive";
import { generateAllPreviewImages } from "./paystubPreviewGenerator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The recurring pay stub runner. The server keeps the schedules: each due
// period draws a download off the plan and queues a run; this side does the
// part only a browser can — generate the PDF — then archives it to the
// run's business and marks the run done. It rides the app's 30s tick, so
// due stubs appear shortly after the app opens.
//
// A run is a US pay stub, a Canadian pay stub or a UK payslip, by the
// employee's country. The employee's Additional options ride along: worker
// type, tax settings (US), deductions, contributions, benefits and absence
// plans. The Canadian and UK generators load on demand.

export const runCountry = (run) => (run.country || run.employee?.country || "us");

// the documentType a run archives as, and the generator that makes it
const DOC_TYPES = { us: "paystub", ca: "canadian-paystub", uk: "uk-payslip" };
const fresh = (list) => (Array.isArray(list) ? list : []).map((x, i) => ({ ...x, id: Date.now() + i }));
const setTax = (t) => Object.fromEntries(Object.entries(t || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));

export const buildFormData = (run) => {
  const emp = run.employee || {};
  const biz = run.business || {};
  const country = runCountry(run);
  const common = {
    name: emp.name || "", employeeId: emp.employeeId || "",
    address: emp.address || "", city: emp.city || "",
    bankName: emp.bankName || "", bank: emp.bankLast4 || "",
    company: biz.name || "", companyAddress: biz.address || "", companyCity: biz.city || "", companyPhone: biz.phone || "",
    payType: emp.payType || "hourly", rate: emp.rate || "", annualSalary: emp.annualSalary || "",
    payFrequency: run.payFrequency || "biweekly", payDay: run.payDay || "Friday",
    hireDate: emp.hireDate || run.periodStart,
    startDate: run.periodStart, endDate: run.periodEnd,
    startDateList: run.periodStart, endDateList: run.periodEnd,
    hoursList: emp.payType === "salary" ? "" : String(run.hoursPerPeriod || ""),
    workerType: emp.workerType === "contractor" ? "contractor" : "employee",
    deductions: fresh(emp.deductions), contributions: fresh(emp.contributions),
    absencePlans: fresh(emp.absencePlans), employerBenefits: fresh(emp.employerBenefits),
    ...(biz.logo ? { logoDataUrl: biz.logo } : {}),
  };
  if (country === "ca") {
    return {
      ...common,
      sin: emp.sin || "", province: emp.province || "ON", postalCode: emp.postalCode || "",
      companyProvince: emp.province || "ON", companyPostalCode: "",
      maritalStatus: "single", federalAllowances: "0", provincialAllowances: "0",
      federalTaxRate: "", provincialTaxRate: "", cppRate: "", eiRate: "",
    };
  }
  if (country === "uk") {
    return {
      ...common,
      niNumber: emp.niNumber || "", postcode: emp.postcode || "", companyPostcode: "",
      taxCode: "1257L", niCategory: "A", studentLoanPlan: "none", paymentMethod: "bacs",
      payeRate: "", niRate: "",
    };
  }
  return {
    ...common,
    ssn: emp.ssnLast4 || "", state: emp.state || "", zip: emp.zip || "",
    companyState: biz.state || "", companyZip: biz.zip || "",
    federalFilingStatus: "", stateAllowances: "0", includeLocalTax: true,
    ...setTax(emp.taxSettings),
  };
};

async function generate(run, formData) {
  const country = runCountry(run);
  if (country === "ca") {
    const { generateAndDownloadCanadianPaystub } = await import("./canadianPaystubGenerator");
    return generateAndDownloadCanadianPaystub(formData, run.template, 1, true);
  }
  if (country === "uk") {
    const { generateAndDownloadUkPayslip } = await import("./ukPayslipGenerator");
    return generateAndDownloadUkPayslip(formData, run.template, 1, true);
  }
  return generateAndDownloadPaystub(formData, run.template, 1, true);
}

const blobToB64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(String(r.result).split(",")[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

// The last day of the period that starts on `start`: a fixed number of days,
// or a calendar month for monthly pay
export function recurringPeriodEnd(start, frequency) {
  const d = new Date(start);
  const days = { weekly: 7, biweekly: 14, fourweekly: 28 }[frequency];
  if (days) d.setDate(d.getDate() + days - 1);
  else { d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1); }
  return d;
}
// How many periods are already due for a schedule starting on `startIso`:
// those whose pay day (the first pay-day weekday on or after the period's
// end) is today or earlier. The server makes up to 8 of them at once.
export function duePeriodsFrom(startIso, frequency, payDay) {
  const target = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5 }[payDay] ?? 5;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let start = new Date(`${startIso}T00:00:00`);
  let n = 0;
  while (n < 8) {
    const end = recurringPeriodEnd(start, frequency);
    const pay = new Date(end);
    while (pay.getDay() !== target) pay.setDate(pay.getDate() + 1);
    if (pay > today) break;
    n++;
    start = new Date(end); start.setDate(start.getDate() + 1);
  }
  return n;
}

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// A watermarked look at what a schedule's NEXT stub will be — no
// download drawn, nothing saved.
export async function previewRecurringStub(schedule, employee, business) {
  const start = new Date(`${schedule.nextPeriodStart}T00:00:00`);
  const frequency = employee.payFrequency || schedule.payFrequency;
  const run = {
    country: schedule.country || employee.country, employee, business,
    periodStart: iso(start), periodEnd: iso(recurringPeriodEnd(start, frequency)),
    hoursPerPeriod: schedule.hoursPerPeriod,
    payFrequency: frequency, payDay: employee.payDay || schedule.payDay,
  };
  const fd = buildFormData(run);
  const country = runCountry(run);
  let img;
  if (country === "ca") {
    const { generateAllCanadianPreviewImages } = await import("./canadianPaystubPreviewGenerator");
    img = await generateAllCanadianPreviewImages(fd, schedule.template, 1);
  } else if (country === "uk") {
    const { generateAllUkPreviewImages } = await import("./ukPayslipPreviewGenerator");
    img = await generateAllUkPreviewImages(fd, schedule.template, 1);
  } else {
    img = await generateAllPreviewImages(fd, schedule.template, 1);
  }
  return Array.isArray(img) ? img[0] : img;
}

let running = false;
let lastCheck = 0;

export async function processRecurringRuns(force = false) {
  if (running || !isUserLoggedIn()) return;
  if (!force && Date.now() - lastCheck < 60_000) return; // once a minute is plenty
  running = true;
  lastCheck = Date.now();
  try {
    const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs/pending-runs`, { headers: userHeaders() });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.success === false) return;
    let didAny = false;
    for (const run of (d.runs || []).slice(0, 5)) {
      try {
        // the generator stamps last-download keys; a background run must
        // not clobber a purchase's re-download state
        const keepUrl = localStorage.getItem("lastDownloadUrl");
        const keepName = localStorage.getItem("lastDownloadFileName");
        const blob = await generate(run, buildFormData(run));
        if (keepUrl === null) localStorage.removeItem("lastDownloadUrl"); else localStorage.setItem("lastDownloadUrl", keepUrl);
        if (keepName === null) localStorage.removeItem("lastDownloadFileName"); else localStorage.setItem("lastDownloadFileName", keepName);
        if (!(blob instanceof Blob)) continue;
        const country = runCountry(run);
        const word = country === "uk" ? "payslip" : "paystub";
        const fileName = `${(run.employee?.name || "employee").replace(/\s+/g, "-")}-${word}-${run.periodEnd}.pdf`;
        const saved = await fetch(`${BACKEND_URL}/api/user/saved-documents`, {
          method: "POST",
          headers: userHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            documentType: DOC_TYPES[country] || "paystub", fileName, fileData: await blobToB64(blob),
            template: run.template, businessId: run.businessId,
          }),
        });
        const sd = await saved.json().catch(() => ({}));
        if (!saved.ok) continue;
        const docId = sd.documentId || sd.document?.id || "";
        await fetch(`${BACKEND_URL}/api/user/recurring-paystubs/runs/${run.id}/complete`, {
          method: "POST",
          headers: userHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ docId }),
        });
        // the bell entry the server queued flips to ready on this device;
        // other devices pick the archived copy up from the account list
        markNotificationReady(`recur_${run.id}`);
        if (docId) setNotificationDocId(`recur_${run.id}`, docId);
        didAny = true;
      } catch (e) {
        console.error("Recurring stub generation failed:", e);
      }
    }
    if (didAny) emitAppLive({ reason: "download" });
  } catch { /* offline; the next tick retries */ }
  finally { running = false; }
}
